import type { ReactNode } from 'react';
import {
  buildTargetingPreview,
  describeAdvancedTargeting,
  type PreviewCustomLocation,
  type PreviewGeoEntry,
  type PreviewGeoGroup,
} from '../../../lib/metaTargetingInterpret';
import { countryName } from '../countries';

// Feature C — the richer extracted/targeting preview. Renders the FULL geo +
// audience picture from a verbatim targeting payload (Meta-native snake_case OR
// Zernio/kyck camelCase): included locations grouped BY TYPE (cities / regions /
// neighborhoods / places / custom locations) each named with radius, EXCLUDED
// locations named under their own group, plus age / gender / location types /
// advantage audience / interests. Pure display — reads the draft, never mutates.
// Shared by the ad-set card summary AND the extraction modal preview.

// describeAdvancedTargeting items that this richer preview now renders as
// first-class rows — filtered out of the supplementary "advanced" list to avoid
// showing the same fact twice (describeAdvancedTargeting itself is unchanged).
const FIRST_CLASS_ADVANCED = new Set(['Excluded geo zones', 'Custom locations', 'Location types', 'Advantage+ audience']);

const unitAbbr = (u: 'kilometer' | 'mile' | null): string => (u === 'mile' ? 'mi' : 'km');

function entryLabel(e: PreviewGeoEntry): string {
  return e.radius != null && e.radius > 0 ? `${e.name} (+${e.radius}${unitAbbr(e.distanceUnit)})` : e.name;
}

function customLabel(e: PreviewCustomLocation): string {
  const coord =
    e.latitude != null && e.longitude != null ? `${e.latitude}, ${e.longitude}` : (e.label ?? 'Custom location');
  const base = e.label && e.latitude != null && e.longitude != null ? `${e.label} (${coord})` : coord;
  return e.radius != null && e.radius > 0 ? `${base} (+${e.radius}${unitAbbr(e.distanceUnit)})` : base;
}

const titleCase = (s: string): string => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Every named entry across a geo group, used to list excluded locations on one line.
function geoGroupNames(g: PreviewGeoGroup): string[] {
  return [
    ...g.cities.map(entryLabel),
    ...g.regions.map(entryLabel),
    ...g.neighborhoods.map(entryLabel),
    ...g.places.map(entryLabel),
    ...g.customLocations.map(customLabel),
  ];
}

type Row = { label: string; value: ReactNode; tone?: 'exclude' };

export function TargetingPreviewDetails({ rawPayload }: { rawPayload: unknown }) {
  const p = buildTargetingPreview(rawPayload);
  const advanced = describeAdvancedTargeting(rawPayload).filter((a) => !FIRST_CLASS_ADVANCED.has(a.label));

  const rows: Row[] = [];

  // ---- Included locations, grouped by type (only non-empty groups) ----
  if (p.countries.length > 0) rows.push({ label: 'Countries', value: p.countries.map(countryName).join(', ') });
  if (p.included.cities.length > 0) rows.push({ label: 'Cities', value: p.included.cities.map(entryLabel).join(', ') });
  if (p.included.regions.length > 0) rows.push({ label: 'Regions', value: p.included.regions.map(entryLabel).join(', ') });
  if (p.included.neighborhoods.length > 0)
    rows.push({ label: 'Neighborhoods', value: p.included.neighborhoods.map(entryLabel).join(', ') });
  if (p.included.places.length > 0) rows.push({ label: 'Places', value: p.included.places.map(entryLabel).join(', ') });
  if (p.included.customLocations.length > 0)
    rows.push({ label: 'Custom locations', value: p.included.customLocations.map(customLabel).join('; ') });

  // ---- Excluded locations (only when present), clearly marked ----
  const excludedNames = geoGroupNames(p.excluded);
  if (excludedNames.length > 0) rows.push({ label: 'Excluded locations', value: excludedNames.join(', '), tone: 'exclude' });

  // ---- Demographics ----
  rows.push({
    label: 'Age',
    value: p.ageMin !== null || p.ageMax !== null ? `${p.ageMin ?? 18} to ${p.ageMax ?? 65}` : '—',
  });
  rows.push({ label: 'Gender', value: p.gender === 'all' ? 'All' : p.gender === 'male' ? 'Male' : 'Female' });
  if (p.locationTypes.length > 0)
    rows.push({ label: 'Location types', value: p.locationTypes.map(titleCase).join(', ') });
  if (p.advantageAudience !== null)
    rows.push({ label: 'Advantage+ audience', value: p.advantageAudience ? 'On' : 'Off' });

  // ---- Interests / behaviors ----
  if (p.interests.length > 0)
    rows.push({ label: 'Interests', value: p.interests.map((i) => i.name).join(', ') });

  return (
    <div className="space-y-3">
      <dl className="divide-y divide-slate-200/70 rounded-md border border-slate-200 bg-white">
        {rows.map((row) => (
          <div key={row.label} className="flex gap-3 px-3 py-2 text-sm">
            <dt className={`w-32 shrink-0 font-medium ${row.tone === 'exclude' ? 'text-rose-600' : 'text-slate-500'}`}>
              {row.label}
            </dt>
            <dd className={`min-w-0 flex-1 ${row.tone === 'exclude' ? 'text-rose-700' : 'text-slate-800'}`}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
      {advanced.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-slate-600">Advanced targeting preserved (applies at publish):</p>
          <dl className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
            {advanced.map((a) => (
              <div key={a.label} className="flex gap-3 px-3 py-1.5 text-xs">
                <dt className="w-40 shrink-0 font-medium text-slate-500">{a.label}</dt>
                <dd className="min-w-0 flex-1 text-slate-700">{a.detail}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
